import styled from "@emotion/styled";
import MuiButton from "@mui/material/Button";
import MuiTexField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import SimpleAccordion from "@components/accordion";
import AccordionBase from "@components/accordion";
import autoCompleteField from "@components/auto-complete-field";
import Button from "@components/Button";
import RadioButtonGroup from "@components/buttons-radio-group";
import ImprovementsList from "@components/improvements-list";
import TextField from "@components/text-field";
import { Divider, Grid } from "@mui/material";
import { MoneyInput, NumberInput } from "@components/mask-components";
import { useIntlBusinessTypes } from "shared/models/const/businessTypes";
import { useIntlRealestateStatus } from "shared/models/const/real-estate-status";
import { useWidth } from "custom-hooks/use-width.hook";
import { useTranslations } from "next-intl";
import { useIntlPropertyTypes } from "shared/models/const/property-types";
import { useFormikContext } from "formik";
import SwitchLabels from "@components/switch-labels/formik-switch-labels";
import { CCurrencyTypes } from "shared/models/const/currencyTypes";
import { useMutation } from "@tanstack/react-query";
import { getSector } from "api/sector.request";

import { FormRealEstateFormik } from "../util/form.util";
import CheckBoxGroupV2 from "@components/checkbox-group-v2";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SectorsField from "./SectorField";
import CitiesField from "./CityField";

interface IDesktopForm {
  handleSubmit: () => void;
  handleReset: () => void;
  handleToggleModal: () => void;
}

export const DesktopForm = (props: IDesktopForm) => {
  const width = useWidth();
  const isMobile = ["xs", "sm"].includes(width);
  const translations = useTranslations("formRealestateFilters");

  const CPropertyTypes = useIntlPropertyTypes();
  const ctxF = useFormikContext<any>();
  const { values } = ctxF;

  const cityIdRaw = values?.locations?.[0]?.id;
  const cityId = cityIdRaw ? Number(cityIdRaw) : null;
  const amountMin = values?.amountMin;
  const amountMax = values?.amountMax;

  const priceRangeInvalid = React.useMemo(() => {
    if (!amountMin || !amountMax) return false;
    const min = Number(amountMin);
    const max = Number(amountMax);
    if (Number.isNaN(min) || Number.isNaN(max)) return false;
    return max < min;
  }, [amountMin, amountMax]);

  const handleSubmit = () => {
    props.handleToggleModal();
    setTimeout(() => {
      props.handleSubmit();
    }, 500);
  };

  const handleReset = () => {
    props.handleToggleModal();
    setTimeout(() => {
      ctxF.setValues({
        ...FormRealEstateFormik.initialValues,
        bathrooms: null,
        halfBathrooms: null,
        bedrooms: null,
        floorLevels: null,
        parkings: null,
      });

      props.handleReset();
    }, 500);
  };

  return (
    <StyledWrapper className="p-15">
      <Grid container item xs={12} spacing={1} className="m-t-5 m-b-20">
        {isMobile && (
          <Grid item xs={12}>
            <StyledCustomAutoComp
              name="typeProperty"
              limitTags={1}
              multiple={true}
              getOptionLabel={(option: any) => option?.name}
              placeholder={translations("typePlaceholder")}
              options={CPropertyTypes}
              className="typeProperty-autocomplete"
            ></StyledCustomAutoComp>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>

      <CheckBoxGroupV2
        column={3}
        options={[
          {
            label: translations("underConstruction"),
            value: false,
          },
          {
            label: translations("built"),
            value: true,
          },
        ]}
        name="onlyUnits"
        checkBoxProps={{
          icon: <RadioButtonUncheckedIcon sx={{ color: "gray" }} />,
          checkedIcon: (
            <RadioButtonCheckedIcon
              sx={{ color: "var(--color-brand-green-main)" }}
            />
          ),
        }}
      />

      <div style={{ marginTop: "15px" }}>
        <CheckBoxGroupV2
          column={3}
          options={[
            {
              label: translations("furnished"),
              value: 1,
            },
            {
              label: translations("whiteLine"),
              value: 2,
            },
          ]}
          multiple={false}
          name="extraBusinessTypes"
          checkBoxProps={{
            icon: <RadioButtonUncheckedIcon sx={{ color: "gray" }} />,
            checkedIcon: (
              <RadioButtonCheckedIcon
                sx={{ color: "var(--color-brand-green-main)" }}
              />
            ),
          }}
        />
      </div>

      <Grid container spacing={3} className="p-t-15">
        <Grid item sm={4} xs={12}>
          <TextField
            placeholder={translations("baths")}
            name="bathrooms"
            inputComponent={NumberInput}
          ></TextField>
        </Grid>

        <Grid item sm={4} xs={12}>
          <HalfBatRoom />
        </Grid>
        <Grid item sm={4} xs={12}>
          <BedRoom />
        </Grid>

        <Grid item sm={4} xs={12}>
          <TextField
            placeholder={translations("parkings")}
            name="parkings"
            inputComponent={NumberInput}
          ></TextField>
        </Grid>

        <Grid item sm={4} xs={12}>
          <TextField
            placeholder={translations("level")}
            inputComponent={NumberInput}
            name="floorLevels"
          ></TextField>
        </Grid>

        <Grid item xs={12}>
          <LocationRow>
            <LocationField>
              <CitiesField name="locations" limitTags={1} />
            </LocationField>

            <LocationField>
              <SectorsField name="sector" cityId={cityId} limitTags={1} />
            </LocationField>
          </LocationRow>
        </Grid>
      </Grid>

      <Grid container spacing={3} className="p-t-15" alignItems="center">
        <Grid item xs={12}>
          <SectionLabel>{translations("price")}</SectionLabel>
        </Grid>
        <Grid item xs={4}>
          <CurrencySelector>
            <SwitchLabels options={CCurrencyTypes} name="currencyType" />
          </CurrencySelector>
        </Grid>
        <Grid item xs={4}>
          <TextField
            placeholder={translations("minimum")}
            name="amountMin"
            inputComponent={MoneyInput}
          ></TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField
            placeholder={translations("maximum")}
            name="amountMax"
            inputComponent={MoneyInput}
          ></TextField>
        </Grid>

        {priceRangeInvalid && (
          <PriceError>{translations("price_range-error_message")}</PriceError>
        )}
      </Grid>

      <Grid item xs={12} style={{ width: "100%", margin: "auto" }}>
        <ImprovementsList columns={3} />
      </Grid>

      <Grid
        container
        justifyContent="space-around"
        className="p-v-15 fixed-bottom b-0"
        alignItems="center"
      >
        <DeleteButton
          variant="text"
          onClick={handleReset}
          sx={{
            color: "var(--color-brand-blue-main)",
          }}
        >
          {translations("delete")}
        </DeleteButton>

        {/* <Button
          type="button"
          onClick={handleSubmit}
          fs="12px"
          bg="--color-brand-red-main"
          thin_button={true}
          sx={{padding:2}}
        >
          
        </Button> */}
        <MuiButton
          onClick={handleSubmit}
          sx={{
            background: "var(--color-brand-red-secondary)",
            color: "white",
            borderRadius: 10,
            fontSize: 12,
            px: 4,
            height: 35,
            // py:-4
          }}
        >
          {translations("apply")}
        </MuiButton>
      </Grid>
    </StyledWrapper>
  );
};

const Sector = () => {
  return;
};

const BedRoom = () => {
  const { setFieldValue, values } = useFormikContext<any>();
  const [value, setValues] = useState<any>();
  const translations = useTranslations("formRealestateFilters");
  const changeBedroom = (e: any) => {
    const value = e.target.value;

    // Solo acepta números (enteros o decimales)
    const numericValue = value.replace(/[^0-9]/g, "");

    // Opcional: convertir a número
    const numberValue = numericValue ? Number(numericValue) : "";
    setFieldValue("bedrooms", numberValue);
    setValues(numberValue);
  };

  useEffect(() => {
    if (typeof values.bedrooms == "number") {
      setValues(values.bedrooms);
    }
    if (Array.isArray(values.bedrooms) && values?.bedrooms?.length) {
      setValues(Number(values.bedrooms[0]));
    }
  }, []);

  return (
    <TextField
      placeholder={translations("bedrooms")}
      name="bedrooms"
      onChange={changeBedroom}
      value={value}
    />
  );
};
const HalfBatRoom = () => {
  const { setFieldValue, values } = useFormikContext<any>();
  const [value, setValues] = useState<any>();
  const translations = useTranslations("formRealestateFilters");

  const changeHalfBatRoom = (e: any) => {
    const value = e.target.value;

    // Solo acepta números (enteros o decimales)
    const numericValue = value.replace(/[^0-9]/g, "");

    // Opcional: convertir a número
    const numberValue = numericValue ? Number(numericValue) : "";
    setFieldValue("halfBathrooms", numberValue);
    setValues(numberValue);
  };

  useEffect(() => {
    if (typeof values.halfBathrooms == "number") {
      setValues(values.halfBathrooms);
    }
    if (Array.isArray(values.halfBathrooms) && values?.halfBathrooms?.length) {
      setValues(Number(values.halfBathrooms[0]));
    }
  }, []);

  return (
    <TextField
      placeholder={translations("half_baths")}
      name="halfBathrooms"
      onChange={changeHalfBatRoom}
      value={value}
    />
  );
};

const StyledWrapper = styled.div`
  .MuiInputBase-input {
    &::placeholder {
      color: #737373;
      font-weight: 400;
      font-size: 14px;
      padding-left: 10px;
    }
  }
`;

const LocationRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-start;
  width: 100%;
`;

const LocationField = styled.div`
  flex: 1;

  .MuiAutocomplete-root {
    width: 100%;
  }
`;

const adapterSectors = (data: any[] = []) =>
  data.map((item) => ({
    id: item.id,
    description: item.description,
    city_id: item.city_id,
  }));

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-brand-blue-main);
`;

const CurrencySelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PriceError = styled.span`
  font-size: 0.8125rem;
  color: var(--color-brand-red-secondary);
  font-weight: 500;
`;

const DeleteButton = styled(MuiButton)`
  text-decoration: underline;
  font-weight: 700;
  font-size: 11px;
  padding-top: 20px;
  @media screen and (max-width: 475px) {
    margin-left: 0;
    margin-top: 15px;
  }
`;

const StyledRadioGroup = styled(RadioButtonGroup)`
  & .MuiFormControlLabel-root {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const StyledCustomAutoComp = styled(autoCompleteField)`
  fieldset {
    border-radius: 50px;
  }
  input::placeholder {
    color: #000;
    opacity: 1;
    font-weight: 600;
    font-size: 14px;
    padding-left: 10px;
  }
`;

// remove before tree weeks

// const styledAmount = {
//   xl: 4,
//   lg: 4,
//   md: 4,
//   sm: 6,
//   xs: 12,
//   sx: { marginTop: "26px" },
// };

// {
//   /* <Grid container sx={{ paddingY: 2 }} alignItems={"center"} spacing={1}>
//         <Grid container item xs={4}>
//           <LabelField label={translations("currency")} />
//           <Grid item xs={12} className="p-t-5 p-l-5">
//             <RadioButtonGroup name="currencyType" items={CCurrencyTypes} />
//           </Grid>
//         </Grid>

//         <Grid item {...styledAmount}>
//           <TextField
//             placeholder={translations("minimum")}
//             inputComponent={MoneyInput}
//             name="amountMin"
//           ></TextField>
//         </Grid>

//         <Grid item {...styledAmount}>
//           <TextField
//             inputComponent={MoneyInput}
//             placeholder={translations("maximum")}
//             name="amountMax"
//           ></TextField>
//         </Grid>
//       </Grid> */
// }

// {
//   /* <Divider />
//       <Grid
//         container
//         alignItems="center"
//         sx={{ paddingTop: "15px", paddingBottom: "25px" }}
//       >
//         <LabelField label="Quiero" />
//         <Grid item className="p-t-5 p-l-5">
//           <StyledRadioGroup name="businessTypes" items={itemsBusiness} />
//         </Grid>
//       </Grid>

//       <Divider /> */
// }

// {
//   /* <Grid item xl={4} lg={4} md={4} sm={12} xs={12}>
//           <TextField
//             placeholder={translations("bedrooms")}
//             name="bedrooms"
//             inputComponent={NumberInput}
//           ></TextField>
//         </Grid> */
// }
